/* eslint-disable jsx-a11y/heading-has-content */
import _ from "lodash";
import ReactMarkdown from "react-markdown";
import { FC, useEffect } from "react";
import { Checkbox, Collapse, CollapseProps, Modal } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";

import PanelForm from "./panel-form";

import useCommonStore from "stores/useCommon";
import QUERY_KEYS from "services/api/queryKeys";
import { getConfigEditionPanel } from "services/api/module/mock.api";

const EditionPanelModal: FC = () => {
  const { isShowEditionPanel, setIsShowEditionPanel, ctrlId, setCtrlId } =
    useCommonStore();
  const { id } = useParams();

  const { data } = useQuery({
    queryKey: [QUERY_KEYS.GET_CONFIG_EDITION_PANEL, id, ctrlId],
    queryFn: () => getConfigEditionPanel(String(id), ctrlId),
    enabled: !!id && !!ctrlId,
  });

  useEffect(() => {
    if (data?.intro) {
      console.log("Raw intro data:", data.intro);
      console.log("Intro type:", typeof data.intro);
    }
  }, [data]);

  useEffect(() => {
    if (!isShowEditionPanel) {
      setCtrlId("");
    }
  }, [isShowEditionPanel, setCtrlId]);

  const items: CollapseProps["items"] = _.get(data, "scope", []).map(
    (item: any) => ({
      key: item.id,
      label: (
        <div className="flex items-center justify-between gap-6">
          <p className="capitalize">{item.title}</p>
          {item.selected && <Checkbox disabled checked={item.selected} />}
        </div>
      ),
      children: (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ node, ...props }) => <p {...props} />,
            h1: ({ node, ...props }) => (
              <h1 className="text-2xl font-bold mb-2" {...props} />
            ),
            h2: ({ node, ...props }) => (
              <h2 className="text-xl font-bold mb-2" {...props} />
            ),
            h3: ({ node, ...props }) => (
              <h3 className="text-lg font-bold mb-2" {...props} />
            ),
            ul: ({ node, ...props }) => (
              <ul className="list-disc ml-4 mb-2" {...props} />
            ),
            ol: ({ node, ...props }) => (
              <ol className="list-decimal ml-4 mb-2" {...props} />
            ),
            pre: ({ node, ...props }) => (
              <pre
                className="bg-gray-100 p-2 rounded mb-2 overflow-x-auto"
                {...props}
              />
            ),
            code: ({ node, ...props }) => (
              <code
                className="bg-gray-100 px-1 rounded inline-block max-w-full overflow-x-auto"
                {...props}
              />
            ),
          }}
        >
          {item.description}
        </ReactMarkdown>
      ),
    })
  );

  return (
    <Modal
      centered
      title={
        <div className="flex items-center gap-2" style={{marginBottom: "24px"}}>
          <EditOutlined />
          <span>Editer la réponse</span>
        </div>
      }
      onCancel={() => {
        setIsShowEditionPanel(false);
      }}
      open={isShowEditionPanel}
      okButtonProps={{
        style: { display: "none" }, // Hide the OK button
      }}
      cancelButtonProps={{
        style: { display: "none" }, // Hide the Cancel button
      }}
      wrapClassName="drawer-edition-panel"
      width="50%"
      styles={{
        body: { padding: "12px 16px" }
      }}
      zIndex={9999}
    >
      {_.get(data, "intro") && (() => {
        const rawIntro = String(_.get(data, "intro", ""));
        // Decode HTML entities if present
        const decoded = rawIntro
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&amp;/g, "&");

        // Fix common markdown formatting issues
        const processed = decoded
          .replace(/^#([^\s])/gm, "# $1")           // Add space after # for headers
          .replace(/^##([^\s])/gm, "## $1")         // Add space after ## for h2
          .replace(/^###([^\s])/gm, "### $1")       // Add space after ### for h3
          .replace(/\*\*\s*([^*]+?)\s*\*\*/g, "**$1**")  // Fix bold with extra spaces
          .replace(/\*([^*]+?)\*/g, "*$1*")         // Fix italic
          .trim();

        return (
          <div className="mb-6 max-w-xl mx-auto w-full px-2 sm:px-0">
            <div className="border border-blue-200 rounded-lg p-3 sm:p-5" style={{backgroundColor: "rgba(59, 134, 134, 0.2)"}}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ node, ...props }) => (
                    <p className="text-base leading-relaxed text-gray-700 mb-0" {...props} />
                  ),
                  h1: ({ node, ...props }) => (
                    <h1 className="text-xl font-bold mb-3 text-gray-900 capitalize" {...props} />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2 className="text-lg font-bold mb-2 text-gray-900" {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 className="text-base font-bold mb-2 text-gray-800" {...props} />
                  ),
                  ul: ({ node, ...props }) => (
                    <ul className="list-disc ml-5 mb-2 space-y-1 text-sm" {...props} />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol className="list-decimal ml-5 mb-2 space-y-1 text-sm" {...props} />
                  ),
                  li: ({ node, ...props }) => (
                    <li className="leading-relaxed text-gray-700" {...props} />
                  ),
                  pre: ({ node, ...props }) => (
                    <pre
                      className="bg-gray-800 text-gray-100 p-3 rounded text-sm mb-3 overflow-x-auto"
                      {...props}
                    />
                  ),
                  code: ({ node, ...props }) => (
                    <code
                      className="bg-gray-200 text-gray-800 px-1.5 py-0.5 rounded text-sm"
                      {...props}
                    />
                  ),
                  strong: ({ node, ...props }) => (
                    <strong className="font-semibold text-gray-900" {...props} />
                  ),
                  em: ({ node, ...props }) => (
                    <em className="italic text-gray-700" {...props} />
                  ),
                }}
              >
                {processed}
              </ReactMarkdown>
            </div>
          </div>
        );
      })()}
      {data && <PanelForm editionPanelConfig={_.get(data, "form")} />}
      
    </Modal>
  );
};

export default EditionPanelModal;
